import { tetri } from './exchange'
import { numDateText, type Key } from './i18n'
import { CARD_PROVIDER_LABELS } from './mock-data'
import type { BankCode, HeldCartItem, PaymentMethod, Prepaid, PrepaidMethod, PrepaidMove, PrepaidMoveKind, PrepaidKind, PrepaidStatus, Sale } from './mock-data'

/**
 * 1.57 — money a customer leaves with us: a gift card („სასაჩუქრე ბარათი") or a
 * deposit („წინასწარი გადახდა", a special order or a layaway). ONE mechanism
 * (migrations 174–176, spec docs/superpowers/specs/2026-10-20-prepaid-gift-card-and-deposit-design.md).
 *
 * Taking the money is NOT a sale: it is a debt to the customer until the goods
 * leave. Spending it IS a sale, paid on a third rail beside cash and card
 * (`sales.paid_prepaid`), so turnover, cost and profit land at the handover.
 *
 * Every rule here mirrors the server's (create_sale 175, close_shift 176) and is
 * pinned to the figures of their proof. The server decides; this file lets the
 * till say the same thing before it asks.
 */

export type Rails3 = { cash: number; card: number; prepaid: number }

/** create_sale's label (175): a prepaid alone is 'prepaid', beside cash or card 'mixed'. */
export function prepaidLabel(r: Rails3): PaymentMethod {
  if (r.prepaid > 0) return r.cash + r.card > 0 ? 'mixed' : 'prepaid'
  if (r.cash > 0 && r.card > 0) return 'mixed'
  return r.card > 0 ? 'card' : 'cash'
}

type RailsSale = Pick<Sale, 'total' | 'payment_method' | 'paid_cash' | 'paid_card' | 'paid_prepaid'>

/**
 * The three rails of a return of `amount` against an original paid (partly) from a
 * prepaid — the cumulative rule of `returnRails` (lib/exchange.ts) with a third rail,
 * as create_sale sets it (175, Codex #7): each rail's target over everything refunded
 * so far, less what that rail already gave back; the prepaid first, then cash, the
 * card the rest. The last return lands every rail exactly. The server requires the
 * till's cash and card to equal these (± 0.01).
 */
export function prepaidReturnRails(
  original: RailsSale,
  prior: readonly { total: number; paid_cash?: number | null; paid_prepaid?: number | null }[],
  amount: number,
): Rails3 {
  const o = { prepaid: original.paid_prepaid ?? 0, cash: original.paid_cash ?? 0, card: original.paid_card ?? 0 }
  const whole = o.prepaid + o.cash + o.card
  if (whole <= 0) return { cash: 0, card: tetri(amount), prepaid: 0 }
  const refunded = prior.reduce((s, r) => s + r.total, 0)
  const prepaidBack = prior.reduce((s, r) => s + (r.paid_prepaid ?? 0), 0)
  const cashBack = prior.reduce((s, r) => s + (r.paid_cash ?? 0), 0)
  const upTo = refunded + amount
  const prepaid = Math.min(Math.max(tetri(tetri((upTo * o.prepaid) / whole) - prepaidBack), 0), amount)
  const cash = Math.min(Math.max(tetri(tetri((upTo * o.cash) / whole) - cashBack), 0), tetri(amount - prepaid))
  return { cash: tetri(cash), card: tetri(amount - prepaid - cash), prepaid: tetri(prepaid) }
}

/** How much of a card a sale takes by itself: the card, or what is left to pay, whichever is less. */
export function applicable(balance: number, due: number): number {
  return Math.max(0, tetri(Math.min(balance, due)))
}

/**
 * A return onto an expired card gives it the return day + 30 days (the owner,
 * 2026-10-20) — otherwise the money goes back to a card that refuses it.
 * Dates are `YYYY-MM-DD` (Tbilisi's business day, as the server's).
 */
export function expiryAfterReturn(expires: string | null, today: string): string | null {
  if (!expires || expires >= today) return expires
  const d = new Date(`${today}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 30)
  return d.toISOString().slice(0, 10)
}

export const codeTail = (code: string): string => code.slice(-4)

/** A scanned pre-printed card's code: 6–32 of digits, Latin letters and dashes (174's CHECK). */
export const codeShapeOk = (code: string): boolean => /^[0-9A-Za-z-]{6,32}$/.test(code)

const money = (s: string) => `₾${Number(s).toFixed(2)}`
const dayText = (iso: string) => {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/** The refusals of 174/175 in the till's words; null for anything else. */
const PLAIN: [string, Key][] = [
  ['PREPAID_NOT_FOUND', 'pp.err.notFound'],
  ['PREPAID_CODE_REQUIRED', 'pp.err.codeRequired'],
  ['PREPAID_ONE_ONLY', 'pp.err.oneOnly'],
  ['PREPAID_WITH_CREDIT', 'pp.err.withCredit'],
  ['PREPAID_RAILS_MISMATCH', 'pp.err.rails'],
  ['PREPAID_FISCAL_UNSUPPORTED', 'pp.err.fiscal'],
  ['PREPAID_NOT_HERE', 'pp.err.notHere'],
  ['PREPAID_CODE_TAKEN', 'pp.err.codeTaken'],
  ['PREPAID_CODE_SHAPE', 'pp.err.codeShape'],
  ['PREPAID_FIELDS', 'pp.err.fields'],
  ['PREPAID_EXPIRY_PAST', 'pp.err.expiryPast'],
  ['PREPAID_METHOD', 'pp.err.method'],
  ['EXCHANGE_PREPAID_SALE', 'pp.err.exchange'],
  ['PREPAID_OFF', 'pp.err.off'],   // 177 — the owner switched it off; new money is not taken
]

export function prepaidErrorKey(message: string | null | undefined): { key: Key; vars?: Record<string, string> } | null {
  if (!message) return null
  const short = /PREPAID_INSUFFICIENT:([0-9.]+)/.exec(message)
  if (short) return { key: 'pp.err.insufficient', vars: { sum: money(short[1]) } }
  const expired = /PREPAID_EXPIRED:(\d{4}-\d{2}-\d{2})/.exec(message)
  if (expired) return { key: 'pp.err.expired', vars: { date: dayText(expired[1]) } }
  const rails = /RETURN_RAILS_MISMATCH:([0-9.]+):([0-9.]+):([0-9.]+)/.exec(message)
  if (rails) return { key: 'pp.err.returnRails', vars: { cash: money(rails[1]), card: money(rails[2]), prepaid: money(rails[3]) } }
  const plain = PLAIN.find(([code]) => message.includes(code))
  return plain ? { key: plain[1] } : null
}

/**
 * The change from cash handed over: what was received, less the CASH rail (not the
 * total — a card paid the rest), less the tetri the till kept (168). Null when no
 * note was typed.
 */
export function changeDue(r: { cashReceived: number | null; paidCash: number; kept: number }): number | null {
  if (r.cashReceived == null) return null
  return tetri(r.cashReceived - r.paidCash - r.kept)
}

const numOr0 = (v: unknown) => (v == null ? 0 : Number(v))

/** A row of list_prepaids / find_prepaid / issue_prepaid as the till holds it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPrepaid(r: any): Prepaid {
  return {
    id: r.id,
    kind: r.kind as PrepaidKind,
    code: r.code ?? '',
    code_tail: r.code_tail ?? codeTail(r.code ?? ''),
    customer_id: r.customer_id ?? null,
    customer_name: r.customer_name ?? null,
    customer_phone: r.customer_phone ?? null,
    note: r.note ?? null,
    items: Array.isArray(r.items) ? (r.items as HeldCartItem[]) : [],
    due_date: r.due_date ?? null,
    expires_at: r.expires_at ?? null,
    amount: numOr0(r.amount),
    balance: numOr0(r.balance),
    status: (r.status ?? 'open') as PrepaidStatus,
    branch_id: r.branch_id ?? null,
    shift_id: r.shift_id ?? null,
    cashier_name: r.cashier_name ?? null,
    created_at: r.created_at,
  }
}

/**
 * What a return gave back to a gift card or a deposit — the share (the server's own rail on the
 * return row), the card, its balance now and its expiry (an expired card gets the return day +
 * 30, 175). Null when the return touched no prepaid. Codex's final review #4: the reply carried
 * all of it and the till threw it away, so nobody could tell the customer until when to spend it.
 */
export function returnedToPrepaid(ret: Pick<Sale, 'paid_prepaid' | 'prepaid_after'>): { amount: number; tail: string; balance: number; expires: string | null } | null {
  const after = ret.prepaid_after
  if (!after || (ret.paid_prepaid ?? 0) < 0.005) return null
  return { amount: ret.paid_prepaid ?? 0, tail: after.code_tail, balance: after.balance, expires: after.expires_at }
}

/** A row of prepaid_moves. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPrepaidMove(r: any): PrepaidMove {
  return {
    id: r.id,
    prepaid_id: r.prepaid_id,
    kind: r.kind as PrepaidMoveKind,
    amount: numOr0(r.amount),
    method: (r.method ?? null) as PrepaidMethod | null,
    card_provider: (r.card_provider ?? null) as BankCode | null,
    shift_id: r.shift_id ?? null,
    sale_id: r.sale_id ?? null,
    branch_id: r.branch_id ?? null,
    cashier_name: r.cashier_name ?? null,
    note: r.note ?? null,
    created_at: r.created_at,
  }
}

/**
 * „წინასწარ მიღებული — გვმართებს" (accounting): what the shop holds for its customers NOW.
 * The ledger is the source — a prepaid's balance is Σ its moves (174) — and every member
 * reads the moves, so the total and the count are exact whatever the list returns. The
 * split by kind needs each open prepaid's kind (list_prepaids, newest 200): when one is
 * missing the split is null, never a split that does not add up.
 */
export type PrepaidOwed = { total: number; count: number; gift: number | null; deposit: number | null }

export function prepaidOwed(moves: readonly PrepaidMove[], kinds?: ReadonlyMap<string, PrepaidKind>): PrepaidOwed {
  const balance = new Map<string, number>()
  for (const m of moves) balance.set(m.prepaid_id, (balance.get(m.prepaid_id) ?? 0) + m.amount)
  let total = 0, count = 0, gift = 0, deposit = 0, known = true
  for (const [id, b] of balance) {
    if (tetri(b) < 0.005) continue
    total += b
    count += 1
    const kind = kinds?.get(id)
    if (kind === 'gift') gift += b
    else if (kind === 'deposit') deposit += b
    else known = false
  }
  return { total: tetri(total), count, gift: known ? tetri(gift) : null, deposit: known ? tetri(deposit) : null }
}

const MOVE_WORD: Record<PrepaidMoveKind, string> = {
  issue: 'მიღება', redeem: 'გაყიდვაში დაიხარჯა', return: 'დაბრუნებით ჩაირიცხა', refund: 'თანხა დაუბრუნდა',
}
const METHOD_WORD: Record<PrepaidMethod, string> = { cash: 'ნაღდი', card: 'ბარათი', transfer: 'გადარიცხვა' }

/**
 * The accountant's Excel sheet „წინასწარი გადახდები": one row per move, oldest first. A
 * document, so Georgian on every device. A gift card is named by its last four only — its
 * code is a bearer secret and a workbook gets e-mailed; a deposit by its customer. A move
 * whose prepaid the list did not carry is still a row, named by the prepaid's id.
 */
export function prepaidMoveRows(
  moves: readonly PrepaidMove[],
  prepaids: ReadonlyMap<string, Pick<Prepaid, 'kind' | 'code_tail' | 'customer_name'>>,
): Record<string, string | number>[] {
  return [...moves]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .map((m) => {
      const p = prepaids.get(m.prepaid_id)
      return {
        'თარიღი': numDateText(m.created_at, { time: true }, 'ka'),
        'სახე': p ? (p.kind === 'gift' ? 'სასაჩუქრე ბარათი' : 'წინასწარი გადახდა') : '',
        'კოდი/კლიენტი': p ? (p.kind === 'gift' ? `••••${p.code_tail}` : p.customer_name ?? '') : m.prepaid_id.slice(0, 8).toUpperCase(),
        'მოძრაობა': MOVE_WORD[m.kind],
        'თანხა': tetri(m.amount),
        'გადახდა': m.method ? METHOD_WORD[m.method] : '',
        'ბანკი': m.card_provider ? CARD_PROVIDER_LABELS[m.card_provider] : '',
        'მოლარე': m.cashier_name ?? '',
      }
    })
}
